import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './useAuth';

export interface TenantConfig {
  // Regional & Currency Settings
  locale: string;
  currency: string;
  country: string;
  timezone: string;
  
  // Number & Date Formatting
  numberFormat: {
    thousandsSeparator: string;
    decimalSeparator: string;
    currencySymbol: string;
    currencyPosition: 'before' | 'after';
  };

  // Precision Configuration
  precision: {
    // Internal calculation precision (always high for accuracy)
    calculation: number; // e.g., 4 decimals for internal calculations
    // Display precision for different value types
    display: {
      currency: number; // e.g., 2 decimals for money display
      quantity: number; // e.g., 4 decimals for quantities
      percentage: number; // e.g., 1 decimal for percentages
      tax: number; // e.g., 4 decimals for tax calculations
      unitPrice: number; // e.g., 4 decimals for unit prices
    };
  };
  
  // Business Settings
  business: {
    taxRate: number;
    fiscalYearStart: string; // MM-DD format
    workingDays: number[];
    businessHours: {
      start: string;
      end: string;
    };
  };
  
  // Compliance & Legal
  compliance: {
    requiresNIT: boolean;
    requiresCUI: boolean;
    invoicePrefix: string;
    taxIdLabel: string;
  };
  
  // Feature Flags per Tenant
  features: {
    multiCurrency: boolean;
    inventoryTracking: boolean;
    advancedReporting: boolean;
    multiLocation: boolean;
    payroll: boolean;
  };
}

const DEFAULT_CONFIGS: Record<string, TenantConfig> = {
  'GT': {
    locale: 'es-GT',
    currency: 'GTQ',
    country: 'GT',
    timezone: 'America/Guatemala',
    numberFormat: {
      thousandsSeparator: ',',
      decimalSeparator: '.',
      currencySymbol: 'Q',
      currencyPosition: 'before'
    },
    precision: {
      calculation: 4, // 4 decimals for internal calculations
      display: {
        currency: 2, // 2 decimals for currency display (Q187.50)
        quantity: 4, // 4 decimals for quantities (1.2500)
        percentage: 1, // 1 decimal for percentages (25.0%)
        tax: 4, // 4 decimals for tax calculations
        unitPrice: 4, // 4 decimals for unit prices
      }
    },
    business: {
      taxRate: 0.12, // IVA 12%
      fiscalYearStart: '01-01',
      workingDays: [1, 2, 3, 4, 5], // Monday to Friday
      businessHours: {
        start: '08:00',
        end: '17:00'
      }
    },
    compliance: {
      requiresNIT: true,
      requiresCUI: true,
      invoicePrefix: 'DTE-',
      taxIdLabel: 'NIT'
    },
    features: {
      multiCurrency: false,
      inventoryTracking: true,
      advancedReporting: true,
      multiLocation: true,
      payroll: true
    }
  },
  'US': {
    locale: 'en-US',
    currency: 'USD',
    country: 'US',
    timezone: 'America/New_York',
    numberFormat: {
      thousandsSeparator: ',',
      decimalSeparator: '.',
      currencySymbol: '$',
      currencyPosition: 'before'
    },
    precision: {
      calculation: 4,
      display: {
        currency: 2,
        quantity: 4,
        percentage: 1,
        tax: 4,
        unitPrice: 4,
      }
    },
    business: {
      taxRate: 0.08, // Average sales tax
      fiscalYearStart: '01-01',
      workingDays: [1, 2, 3, 4, 5],
      businessHours: {
        start: '09:00',
        end: '17:00'
      }
    },
    compliance: {
      requiresNIT: false,
      requiresCUI: false,
      invoicePrefix: 'INV-',
      taxIdLabel: 'Tax ID'
    },
    features: {
      multiCurrency: true,
      inventoryTracking: true,
      advancedReporting: true,
      multiLocation: true,
      payroll: true
    }
  },
  'MX': {
    locale: 'es-MX',
    currency: 'MXN',
    country: 'MX',
    timezone: 'America/Mexico_City',
    numberFormat: {
      thousandsSeparator: ',',
      decimalSeparator: '.',
      currencySymbol: '$',
      currencyPosition: 'before'
    },
    precision: {
      calculation: 4,
      display: {
        currency: 2,
        quantity: 4,
        percentage: 1,
        tax: 4,
        unitPrice: 4,
      }
    },
    business: {
      taxRate: 0.16, // IVA 16%
      fiscalYearStart: '01-01',
      workingDays: [1, 2, 3, 4, 5],
      businessHours: {
        start: '08:00',
        end: '18:00'
      }
    },
    compliance: {
      requiresNIT: false,
      requiresCUI: false,
      invoicePrefix: 'CFDI-',
      taxIdLabel: 'RFC'
    },
    features: {
      multiCurrency: true,
      inventoryTracking: true,
      advancedReporting: true,
      multiLocation: true,
      payroll: true
    }
  }
};

interface TenantConfigContextType {
  config: TenantConfig;
  loading: boolean;
  updateConfig: (updates: Partial<TenantConfig>) => Promise<void>;
  formatCurrency: (amount: number, options?: { compact?: boolean; showSign?: boolean }) => string;
  formatNumber: (value: number, options?: { precision?: number; compact?: boolean }) => string;
  formatPercentage: (value: number, options?: { precision?: number; showSign?: boolean }) => string;
  // New specific formatting functions
  formatUnitPrice: (price: number, options?: { compact?: boolean; showSign?: boolean }) => string;
  formatQuantity: (quantity: number, options?: { compact?: boolean }) => string;
  formatTaxAmount: (amount: number, options?: { compact?: boolean; showSign?: boolean }) => string;
  // Calculation helpers
  roundForCalculation: (value: number) => number;
  roundForDisplay: (value: number, type: keyof TenantConfig['precision']['display']) => number;
}

const TenantConfigContext = createContext<TenantConfigContextType | undefined>(undefined);

interface TenantConfigProviderProps {
  children: ReactNode;
}

export function TenantConfigProvider({ children }: TenantConfigProviderProps) {
  const { selectedCompany, user } = useAuth();
  const [config, setConfig] = useState<TenantConfig>(DEFAULT_CONFIGS['GT']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTenantConfig = () => {
      if (!selectedCompany) {
        setConfig(DEFAULT_CONFIGS['GT']);
        setLoading(false);
        return;
      }

      // Get config based on company country/currency
      const baseConfig = DEFAULT_CONFIGS[selectedCompany.country] || DEFAULT_CONFIGS['GT'];
      
      // Override with company-specific settings
      const tenantConfig: TenantConfig = {
        ...baseConfig,
        currency: selectedCompany.currency || baseConfig.currency,
        country: selectedCompany.country || baseConfig.country,
        
        // Allow company-specific overrides from menu_config
        ...(selectedCompany.menu_config?.tenantConfig || {})
      };

      setConfig(tenantConfig);
      setLoading(false);
    };

    loadTenantConfig();
  }, [selectedCompany]);

  const updateConfig = async (updates: Partial<TenantConfig>) => {
    // This would typically save to database
    setConfig(prevConfig => ({
      ...prevConfig,
      ...updates
    }));
  };

  const formatCurrency = (amount: number, options: { compact?: boolean; showSign?: boolean } = {}) => {
    const { compact = false, showSign = false } = options;
    const precision = config.precision.display.currency;
    
    try {
      const formatter = new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: compact && Math.abs(amount) >= 1000 ? 0 : precision,
        maximumFractionDigits: compact && Math.abs(amount) >= 1000 ? 2 : precision,
        signDisplay: showSign ? 'exceptZero' : 'auto'
      });

      if (compact && Math.abs(amount) >= 1000) {
        if (Math.abs(amount) >= 1e9) {
          return `${config.numberFormat.currencySymbol}${(amount / 1e9).toFixed(1)}B`;
        }
        if (Math.abs(amount) >= 1e6) {
          return `${config.numberFormat.currencySymbol}${(amount / 1e6).toFixed(1)}M`;
        }
        if (Math.abs(amount) >= 1e3) {
          return `${config.numberFormat.currencySymbol}${(amount / 1e3).toFixed(1)}K`;
        }
      }

      return formatter.format(amount);
    } catch (error) {
      // Fallback formatting
      const sign = amount < 0 ? '-' : (showSign && amount > 0 ? '+' : '');
      const absAmount = Math.abs(amount);
      return `${sign}${config.numberFormat.currencySymbol}${absAmount.toFixed(precision)}`;
    }
  };

  const formatNumber = (value: number, options: { precision?: number; compact?: boolean } = {}) => {
    const { precision = 2, compact = false } = options;
    
    try {
      if (compact && Math.abs(value) >= 1000) {
        if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
        if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
        if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
      }

      return new Intl.NumberFormat(config.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
      }).format(value);
    } catch (error) {
      return value.toFixed(precision);
    }
  };

  const formatPercentage = (value: number, options: { precision?: number; showSign?: boolean } = {}) => {
    const { precision = config.precision.display.percentage, showSign = true } = options;
    
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
        signDisplay: showSign ? 'exceptZero' : 'auto'
      }).format(value / 100);
    } catch (error) {
      const sign = showSign && value > 0 ? '+' : '';
      return `${sign}${value.toFixed(precision)}%`;
    }
  };

  // New specific formatting functions
  const formatUnitPrice = (price: number, options: { compact?: boolean; showSign?: boolean } = {}) => {
    const { compact = false, showSign = false } = options;
    const precision = config.precision.display.unitPrice;
    
    try {
      const formatter = new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: compact ? 0 : precision,
        maximumFractionDigits: precision,
        signDisplay: showSign ? 'exceptZero' : 'auto'
      });

      return formatter.format(price);
    } catch (error) {
      const sign = price < 0 ? '-' : (showSign && price > 0 ? '+' : '');
      const absPrice = Math.abs(price);
      return `${sign}${config.numberFormat.currencySymbol}${absPrice.toFixed(precision)}`;
    }
  };

  const formatQuantity = (quantity: number, options: { compact?: boolean } = {}) => {
    const { compact = false } = options;
    const precision = config.precision.display.quantity;
    
    try {
      if (compact && Math.abs(quantity) >= 1000) {
        if (Math.abs(quantity) >= 1e9) return `${(quantity / 1e9).toFixed(1)}B`;
        if (Math.abs(quantity) >= 1e6) return `${(quantity / 1e6).toFixed(1)}M`;
        if (Math.abs(quantity) >= 1e3) return `${(quantity / 1e3).toFixed(1)}K`;
      }

      // Remove trailing zeros for quantities
      const formatted = quantity.toFixed(precision);
      return parseFloat(formatted).toString();
    } catch (error) {
      return quantity.toFixed(precision);
    }
  };

  const formatTaxAmount = (amount: number, options: { compact?: boolean; showSign?: boolean } = {}) => {
    const { compact = false, showSign = false } = options;
    const precision = config.precision.display.tax;
    
    try {
      const formatter = new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: compact ? 0 : precision,
        maximumFractionDigits: precision,
        signDisplay: showSign ? 'exceptZero' : 'auto'
      });

      return formatter.format(amount);
    } catch (error) {
      const sign = amount < 0 ? '-' : (showSign && amount > 0 ? '+' : '');
      const absAmount = Math.abs(amount);
      return `${sign}${config.numberFormat.currencySymbol}${absAmount.toFixed(precision)}`;
    }
  };

  // Calculation helpers
  const roundForCalculation = (value: number): number => {
    const precision = config.precision.calculation;
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
  };

  const roundForDisplay = (value: number, type: keyof TenantConfig['precision']['display']): number => {
    const precision = config.precision.display[type];
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
  };

  return (
    <TenantConfigContext.Provider value={{
      config,
      loading,
      updateConfig,
      formatCurrency,
      formatNumber,
      formatPercentage,
      formatUnitPrice,
      formatQuantity,
      formatTaxAmount,
      roundForCalculation,
      roundForDisplay
    }}>
      {children}
    </TenantConfigContext.Provider>
  );
}

export function useTenantConfig() {
  const context = useContext(TenantConfigContext);
  if (context === undefined) {
    throw new Error('useTenantConfig must be used within a TenantConfigProvider');
  }
  return context;
}

// Utility hooks for common operations
export function useCurrencyFormatter() {
  const { formatCurrency } = useTenantConfig();
  return formatCurrency;
}

export function useNumberFormatter() {
  const { formatNumber } = useTenantConfig();
  return formatNumber;
}

export function usePercentageFormatter() {
  const { formatPercentage } = useTenantConfig();
  return formatPercentage;
}

// New utility hooks for specific formatting needs
export function useUnitPriceFormatter() {
  const { formatUnitPrice } = useTenantConfig();
  return formatUnitPrice;
}

export function useQuantityFormatter() {
  const { formatQuantity } = useTenantConfig();
  return formatQuantity;
}

export function useTaxAmountFormatter() {
  const { formatTaxAmount } = useTenantConfig();
  return formatTaxAmount;
}

export function useCalculationHelpers() {
  const { roundForCalculation, roundForDisplay } = useTenantConfig();
  return { roundForCalculation, roundForDisplay };
}